import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import Button from './ui/Button';

/**
 * Terms of Use & Privacy Policy modal.
 * Content lives in the `auth:legal` locale namespace (EN/FR/AR) and references
 * Algeria's Law 18-07 on the protection of personal data.
 */
/**
 * When opened from the consent banner, `onAccept` is supplied and the footer
 * offers to accept right here. Otherwise reading the terms and agreeing to them
 * were two separate acts in two different places: you clicked Review, read,
 * clicked "I understand" — which only closed the modal — and then had to find
 * the banner again to actually agree. The obvious button did not do the obvious
 * thing.
 */
const TermsPrivacyModal = ({ open, onClose, onAccept, accepting = false }) => {
  const { t } = useTranslation('auth');

  // Lock body scroll while open + close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e?.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const rawSections = t('legal.sections', { returnObjects: true });
  const sections = Array.isArray(rawSections) ? rawSections : [];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('legal.title')}
        className="relative w-full max-w-2xl max-h-[85vh] bg-card border border-border rounded-lg shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Icon name="ShieldCheck" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">{t('legal.title')}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t('legal.updated')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('legal.close')}
            className="text-muted-foreground hover:text-foreground p-1 rounded-md transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-4">
          <p className="text-sm text-foreground leading-relaxed">{t('legal.intro')}</p>

          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 flex items-start gap-2">
            <Icon name="Scale" size={16} className="text-primary flex-shrink-0 mt-0.5" />
            <p className="text-xs text-foreground leading-relaxed">{t('legal.lawRef')}</p>
          </div>

          {sections?.map((section, i) => (
            <div key={i} className="space-y-1">
              <h3 className="text-sm font-semibold text-foreground">{section?.heading}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">
                {section?.body}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex justify-end gap-2">
          {onAccept ? (
            <>
              <Button variant="ghost" onClick={onClose} disabled={accepting}>
                {t('legal.close')}
              </Button>
              <Button
                variant="default"
                onClick={onAccept}
                loading={accepting}
                iconName="Check"
                iconPosition="left"
              >
                {t('legal.ackAndAccept', 'Understood and accept')}
              </Button>
            </>
          ) : (
            <Button variant="default" onClick={onClose}>
              {t('legal.ack')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsPrivacyModal;
